import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase, isAdmin } from '../lib/supabase';

const AdminStatusTest: React.FC = () => {
  const { user, isAdminUser } = useAuth();
  const [testResults, setTestResults] = useState<any>({});
  const [loading, setLoading] = useState(false);

  const runTests = async () => {
    setLoading(true);
    const results: any = {};

    try {
      // 1. 检查用户状态
      results.user = {
        exists: !!user,
        email: user?.email,
        id: user?.id
      };

      // 2. 检查AuthContext中的管理员状态
      results.authContextAdmin = isAdminUser;

      // 3. 直接调用isAdmin函数
      if (user) {
        results.directIsAdmin = await isAdmin(user, true);
      }

      // 4. 检查session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      results.session = {
        exists: !!session,
        error: sessionError?.message,
        hasToken: !!session?.access_token
      };

      // 5. 直接调用check-admin接口
      if (session?.access_token) {
        try {
          const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/check-admin`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
            },
          });
          
          if (response.ok) {
            const data = await response.json();
            results.checkAdminAPI = data;
          } else {
            results.checkAdminAPI = { error: `HTTP ${response.status}` };
          }
        } catch (error) {
          results.checkAdminAPI = { error: error instanceof Error ? error.message : 'Unknown error' };
        }
      }

      setTestResults(results);
    } catch (error) {
      console.error('测试失败:', error);
      results.error = error instanceof Error ? error.message : 'Unknown error';
      setTestResults(results);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      runTests();
    }
  }, [user]);

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">管理员状态调试</h2>
      
      <button 
        onClick={runTests}
        disabled={loading}
        className="mb-4 bg-blue-500 hover:bg-blue-700 disabled:bg-gray-400 text-white font-bold py-2 px-4 rounded"
      >
        {loading ? '测试中...' : '重新测试'}
      </button>

      <div className="space-y-4">
        <div className="bg-gray-50 p-4 rounded">
          <h3 className="font-semibold mb-2">用户状态</h3>
          <pre className="text-sm">{JSON.stringify(testResults.user, null, 2)}</pre>
        </div>

        <div className="bg-gray-50 p-4 rounded">
          <h3 className="font-semibold mb-2">AuthContext管理员状态</h3>
          <p className={`text-lg font-bold ${testResults.authContextAdmin ? 'text-green-600' : 'text-red-600'}`}>
            {testResults.authContextAdmin ? '✅ 是管理员' : '❌ 非管理员'}
          </p>
        </div>

        <div className="bg-gray-50 p-4 rounded">
          <h3 className="font-semibold mb-2">直接调用isAdmin函数</h3>
          <p className={`text-lg font-bold ${testResults.directIsAdmin ? 'text-green-600' : 'text-red-600'}`}>
            {testResults.directIsAdmin ? '✅ 是管理员' : '❌ 非管理员'}
          </p>
        </div>

        <div className="bg-gray-50 p-4 rounded">
          <h3 className="font-semibold mb-2">Session状态</h3>
          <pre className="text-sm">{JSON.stringify(testResults.session, null, 2)}</pre>
        </div>

        <div className="bg-gray-50 p-4 rounded">
          <h3 className="font-semibold mb-2">check-admin API响应</h3>
          <pre className="text-sm">{JSON.stringify(testResults.checkAdminAPI, null, 2)}</pre>
        </div>

        {testResults.error && (
          <div className="bg-red-50 p-4 rounded">
            <h3 className="font-semibold mb-2 text-red-800">错误</h3>
            <p className="text-red-600">{testResults.error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminStatusTest;